import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { CampaignStatus } from '@/services/ws-digital/src/main/resources/rest-api';
import { IntegrationType } from '@/services/ws-header-tag/src/main/resources/rest-api';
import type { AggregatedSearchResultResponse, ValueDto } from '@/services/ws-search/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Icons, Loader } from '@/js/libs/ui/src/V2';
import { and, not, or, PrivateContent, Roles } from '@/js/libs/use-auth';
import { useClickOutside } from '@/js/libs/use-click-outside';

import { ROUTER } from 'src/AppRouter.tsx';
import { SearchListBlock } from './SearchListBlock.tsx';
import { WrapperContent } from './styles.ts';

type Props = {
  data?: AggregatedSearchResultResponse;
  search: string;
  loading?: boolean;
  onNavigate: (path: string) => void;
  onClose: () => void;
};

export const SearchList = memo(function SearchList({ search, data, loading, onNavigate, onClose }: Props) {
  const [t] = useTranslation(['backend', 'common']);
  const { colors } = useTheme();
  const [refPopUpToggleBtn, hasClickedOutsidePopUpToggleBtn] = useClickOutside<HTMLDivElement>();

  const handleCampaignClick = useCallbackRef((id: ValueDto['id']) => {
    onNavigate(ROUTER['/orders/campaigns/:type/:campaignId'].path({ type: 'ALL', campaignId: id }));
  });

  const handleContactClick = useCallbackRef((id: ValueDto['id']) => {
    onNavigate(ROUTER['/crm/contacts/edit/:contactId'].path({ contactId: id }));
  });

  const handleAccountClick = useCallbackRef((id: ValueDto['id']) => {
    onNavigate(ROUTER['/crm/accounts/:accountId'].path({ accountId: id }));
  });

  const handleDigitalCreativeClick = useCallbackRef((id: ValueDto['id']) => {
    onNavigate(ROUTER['/creatives/digital/:creativeId/edit'].path({ creativeId: id }));
  });

  const handleContactShowMoreClick = useCallbackRef(() => {
    onNavigate(ROUTER['/crm/contacts'].path({ search }));
  });

  const handleAccountShowMoreClick = useCallbackRef(() => {
    onNavigate(ROUTER['/crm/accounts'].path({ search }));
  });

  const handleCampaignShowMoreClick = useCallbackRef(() => {
    onNavigate(ROUTER['/orders/campaigns'].path({ search, type: CampaignStatus.ALL }));
  });

  const handleDigitalCreativeShowMoreClick = useCallbackRef(() => {
    onNavigate(ROUTER['/creatives/digital'].path({ search }));
  });

  const handleClientHeaderTagClick = useCallbackRef((id: ValueDto['id']) => {
    onNavigate(
      ROUTER['/header-tag/tags/:integrationType/:headerTagId/placements'].path({
        integrationType: IntegrationType.CLIENT,
        headerTagId: id,
      }),
    );
  });

  const handleClientHeaderTagShowMoreClick = useCallbackRef(() => {
    onNavigate(ROUTER['/header-tag/tags/:integrationType'].path({ integrationType: IntegrationType.CLIENT, searchQuery: search }));
  });

  const handleServerHeaderTagClick = useCallbackRef((id: ValueDto['id']) => {
    onNavigate(
      ROUTER['/header-tag/tags/:integrationType/:headerTagId/placements'].path({
        integrationType: IntegrationType.SERVER,
        headerTagId: id,
      }),
    );
  });

  const handleServerHeaderTagShowMoreClick = useCallbackRef(() => {
    onNavigate(ROUTER['/header-tag/tags/:integrationType'].path({ integrationType: IntegrationType.SERVER, searchQuery: search }));
  });

  const handleWebsiteClick = useCallbackRef((id: ValueDto['id']) => {
    onNavigate(ROUTER['/finances/payouts/websites/:id'].path({ id }));
  });

  const handleWebsiteShowMoreClick = useCallbackRef(() => {
    onNavigate(ROUTER['/finances/payouts/websites'].path({ search }));
  });

  const handlePayoutConfigClick = useCallbackRef((id: ValueDto['id']) => {
    onNavigate(ROUTER['/finances/payouts/payout-configs/:id'].path({ id }));
  });

  const handlePayoutConfigShowMoreClick = useCallbackRef(() => {
    onNavigate(ROUTER['/finances/payouts/payout-configs'].path({ search }));
  });

  useEffect(() => {
    if (hasClickedOutsidePopUpToggleBtn) {
      onClose();
    }
  }, [hasClickedOutsidePopUpToggleBtn, onClose]);

  return loading ? (
    <WrapperContent ref={refPopUpToggleBtn}>
      <Loader fullWidth height={230} />
    </WrapperContent>
  ) : (
    <WrapperContent ref={refPopUpToggleBtn}>
      {data?.accounts && (
        <PrivateContent roles={or(Roles.MANAGE_ACCOUNTS, Roles.SELF_BOOKING)}>
          <SearchListBlock
            items={data.accounts.values}
            icon={<Icons.Person color={colors.purple100} size={24} />}
            onItemClick={handleAccountClick}
            onShowMoreClick={handleAccountShowMoreClick}
            search={search}
            title={t('common:ACCOUNTS')}
            total={data.accounts.total}
            highlightColor={colors.purple10}
            dataTestId='manage-accounts-search-list-block'
          />
        </PrivateContent>
      )}

      {data?.contacts && (
        <PrivateContent roles={Roles.MANAGE_ACCOUNTS}>
          <SearchListBlock
            items={data.contacts.values}
            icon={<Icons.Users color={colors.purpleBlue100} size={24} />}
            onItemClick={handleContactClick}
            onShowMoreClick={handleContactShowMoreClick}
            search={search}
            title={t('common:CONTACTS')}
            total={data.contacts.total}
            highlightColor={colors.purpleBlue10}
            dataTestId='manage-contacts-search-list-block'
          />
        </PrivateContent>
      )}

      {data?.campaigns && (
        <PrivateContent roles={Roles.AGENCY}>
          <SearchListBlock
            items={data.campaigns.values}
            icon={<Icons.Document color={colors.teal100} size={24} />}
            onItemClick={handleCampaignClick}
            onShowMoreClick={handleCampaignShowMoreClick}
            search={search}
            title={t('common:ORDERS')}
            total={data.campaigns.total}
            highlightColor={colors.teal10}
            dataTestId='campaigns-search-list-block'
          />
        </PrivateContent>
      )}

      {data?.digitalCreatives && (
        <PrivateContent roles={or(Roles.MANAGE_DIGITAL_CREATIVES, Roles.SELF_BOOKING)}>
          <SearchListBlock
            items={data.digitalCreatives.values}
            icon={<Icons.Image color={colors.yellow100} size={24} />}
            onItemClick={handleDigitalCreativeClick}
            onShowMoreClick={handleDigitalCreativeShowMoreClick}
            search={search}
            title={t('common:DIGITAL_CREATIVES')}
            total={data.digitalCreatives.total}
            highlightColor={colors.yellow10}
            dataTestId='digital-creatives-search-list-block'
          />
        </PrivateContent>
      )}

      {data?.headerTags && (
        <PrivateContent roles={Roles.HEADER_TAG}>
          <SearchListBlock
            items={data.headerTags.values}
            icon={<Icons.Tag color={colors.pink100} size={24} />}
            onItemClick={handleClientHeaderTagClick}
            onShowMoreClick={handleClientHeaderTagShowMoreClick}
            search={search}
            title={t('common:CLIENT_HEADER_TAGS')}
            total={data.headerTags.total}
            highlightColor={colors.pink10}
            dataTestId='header-tag-list-block'
          />
        </PrivateContent>
      )}

      {data?.serverHeaderTags && (
        <PrivateContent roles={Roles.HEADER_TAG}>
          <SearchListBlock
            items={data.serverHeaderTags.values}
            icon={<Icons.Development color={colors.lightBlue100} size={24} />}
            onItemClick={handleServerHeaderTagClick}
            onShowMoreClick={handleServerHeaderTagShowMoreClick}
            search={search}
            title={t('common:SERVER_HEADER_TAGS')}
            total={data.serverHeaderTags.total}
            highlightColor={colors.lightBlue10}
            dataTestId='server-header-tag-list-block'
          />
        </PrivateContent>
      )}

      {data?.websites && (
        <PrivateContent roles={and(Roles.PAYOUT, not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)))}>
          <SearchListBlock
            items={data.websites.values}
            icon={<Icons.Globe color={colors.green100} size={24} />}
            onItemClick={handleWebsiteClick}
            onShowMoreClick={handleWebsiteShowMoreClick}
            search={search}
            title={t('common:WEBSITES')}
            total={data.websites.total}
            highlightColor={colors.green10}
            dataTestId='websites-search-list-block'
          />
        </PrivateContent>
      )}

      {data?.payoutConfigs && (
        <PrivateContent roles={and(Roles.PAYOUT, not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)))}>
          <SearchListBlock
            items={data.payoutConfigs.values}
            icon={<Icons.Gear color={colors.coral100} size={24} />}
            onItemClick={handlePayoutConfigClick}
            onShowMoreClick={handlePayoutConfigShowMoreClick}
            search={search}
            title={t('common:PAYOUT_CONFIGS')}
            total={data.payoutConfigs.total}
            highlightColor={colors.coral10}
            dataTestId='payout-config-search-list-block'
          />
        </PrivateContent>
      )}
    </WrapperContent>
  );
});
